import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ChatWindow, { CartItemState } from './ChatWindow';
import VoiceOverlay from './VoiceOverlay';
import { useAgentVoiceConversation, VoiceConversation } from './useAgentVoiceConversation';
import {
  AgentProduct,
  ChatMessage,
  RequiresAction,
  createChatSession,
  getChatSession,
  sendChatMessage,
  verifyAgentOtp,
} from '../../api/bookingAgent';
import { addVariantToShopifyCart, getShopifyCartCount, refreshShopifyCartUi } from '../../utils/shopifyCart';
import {
  consumeAgentResume,
  getPersistedAgentSessionId,
  getPersistedAgentStylistCode,
  getPersistedAgentStylistDisplayName,
  parseEmbedResumeFromUrl,
  persistAgentSessionId,
  persistAgentStylistCode,
  persistAgentStylistDisplayName,
} from '../../utils/agentSessionStorage';
import {
  normalizeQuickReplies,
  QuickReplyOption,
} from './quickReplyUtils';
import { getWebOtpHost, useWebOtpAutofill } from './useWebOtpAutofill';
import { getURLstylistCode } from '../../utils/globalFunctions';
import { normalizeAgentApiBase } from '../../utils/agentApiBase';
import { isBookingAgentBuildEnabled } from '../../utils/bookingAgentFlag';
import endpoints from '../../utils/endpoints';

interface ChatWidgetProps {
  stylistUniqueCode?: string;
  shopifyStylistPageId?: string;
  clientId?: string;
  apiBase?: string;
  bookingBaseUrl?: string;
  embedMode?: boolean;
  bookingAgentEnabled?: boolean;
}

const GREETING = "Hi! I'm your personal booking assistant. What are you looking to book today?";

const AGENT_WIDGET_STYLES = `
  @keyframes fab-pulse {
    0% { box-shadow: 0 0 0 0 rgba(45,41,37,0.4); }
    70% { box-shadow: 0 0 0 12px rgba(45,41,37,0); }
    100% { box-shadow: 0 0 0 0 rgba(45,41,37,0); }
  }
  @keyframes slide-up {
    from { opacity: 0; transform: translateY(20px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .husl-agent-fab {
    position: fixed;
    bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    right: 24px;
    z-index: 2147483000;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: #2d2925;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0,0,0,0.22);
    animation: fab-pulse 2.4s ease-out 1s 3;
  }
  .husl-agent-panel {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 2147483000;
    width: 375px;
    height: 580px;
    max-height: calc(100vh - 48px);
    max-width: calc(100vw - 48px);
    display: flex;
    flex-direction: column;
    border-radius: 20px;
    overflow: hidden;
    font-family: inherit;
    box-shadow: 0 16px 48px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.08);
    background: #faf8f5;
    animation: slide-up 0.25s ease-out;
  }
  @media (max-width: 767px) {
    .husl-agent-panel {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      height: 100dvh;
      max-width: 100%;
      max-height: 100%;
      max-height: 100dvh;
      border-radius: 0;
    }
  }
`;

export default function ChatWidget({
  stylistUniqueCode,
  shopifyStylistPageId,
  clientId,
  apiBase,
  embedMode = false,
  bookingAgentEnabled,
}: ChatWidgetProps) {
  const resumeStarted = useRef(false);
  const voiceRef = useRef<VoiceConversation | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isAgentVisible, setIsAgentVisible] = useState<boolean | null>(
    !isBookingAgentBuildEnabled()
      ? false
      : bookingAgentEnabled === undefined
        ? null
        : bookingAgentEnabled,
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [resolvedStylistCode, setResolvedStylistCode] = useState(stylistUniqueCode || '');
  const [stylistDisplayName, setStylistDisplayName] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [requiresAction, setRequiresAction] = useState<RequiresAction | null>(null);
  // Lifted cart-add state: survives ProductCardsInline unmounting (voice/text
  // swaps, requires_action changes) and blocks double-adds of a variant.
  // The ref is the synchronous lock (state snapshots lag behind rapid clicks).
  const [cartAdds, setCartAdds] = useState<Record<string, CartItemState>>({});
  const [voiceQuickReplies, setVoiceQuickReplies] = useState<Array<string | QuickReplyOption>>([]);
  const cartAddsRef = useRef<Record<string, CartItemState>>({});
  const [cartCount, setCartCount] = useState<number | null>(null);
  // Cart-count reconciliation invariant: a fetched count may only apply if it
  // was issued at-or-after the last optimistic mutation AND is newer than the
  // last applied fetch — server state older than a local add never wins.
  const cartCountSeqRef = useRef(0);
  const cartCountAppliedSeqRef = useRef(0);
  const cartMutationSeqRef = useRef(0);
  const [initError, setInitError] = useState(false);

  const chatApiBase = normalizeAgentApiBase(apiBase);

  // Seed the cart count from the storefront when the chat opens in embed
  // mode, so the checkout bar reflects items already in the session cart
  // and the optimistic bump starts from the real base.
  useEffect(() => {
    if (!embedMode || !isOpen) {
      return;
    }
    const seq = ++cartCountSeqRef.current;
    getShopifyCartCount().then((count) => {
      if (
        count !== null
        && seq > cartCountAppliedSeqRef.current
        && seq > cartMutationSeqRef.current
      ) {
        cartCountAppliedSeqRef.current = seq;
        setCartCount(count);
      }
    });
  }, [embedMode, isOpen]);

  useEffect(() => {
    if (!isBookingAgentBuildEnabled()) {
      setIsAgentVisible(false);
      return;
    }

    if (bookingAgentEnabled !== undefined) {
      setIsAgentVisible(bookingAgentEnabled);
      return;
    }

    let cancelled = false;

    async function loadAgentVisibility() {
      try {
        const stylistCode = stylistUniqueCode || getURLstylistCode();
        if (!stylistCode && !shopifyStylistPageId) {
          if (!cancelled) {
            setIsAgentVisible(false);
          }
          return;
        }

        const visibilityBase = chatApiBase || normalizeAgentApiBase(endpoints.API_BASE_URL);
        const params = new URLSearchParams();
        if (shopifyStylistPageId) {
          params.set('shopify_stylist_page_id', shopifyStylistPageId);
        } else {
          params.set('stylist_unique_code', stylistCode);
        }

        const response = await fetch(`${visibilityBase}${endpoints.stylist_location}?${params.toString()}`);
        const data = await response.json();
        if (!cancelled) {
          setIsAgentVisible(Boolean(data?.stylist_info?.booking_agent_enabled));
        }
      } catch {
        if (!cancelled) {
          setIsAgentVisible(false);
        }
      }
    }

    loadAgentVisibility();

    return () => {
      cancelled = true;
    };
  }, [bookingAgentEnabled, chatApiBase, shopifyStylistPageId, stylistUniqueCode]);

  const appendAssistantReply = useCallback((res: Awaited<ReturnType<typeof sendChatMessage>>) => {
    const quickReplies = normalizeQuickReplies(res.quick_replies);
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: res.reply,
      quick_replies: quickReplies,
    }]);
    if (voiceRef.current?.voiceMode) {
      setVoiceQuickReplies(quickReplies);
    }
    if (res.requires_action?.type === 'deposit_payment') {
      setRequiresAction(res.requires_action);
      return;
    }
    if (res.requires_action) {
      setRequiresAction(res.requires_action);
      return;
    }
    if (res.booking_state?.deposit_paid || res.booking_state?.booking_id) {
      setRequiresAction(null);
    }
  }, []);

  const loadExistingSession = useCallback(async (existingSessionId: string) => {
    try {
      const data = await getChatSession(existingSessionId, chatApiBase);
      setSessionId(existingSessionId);
      persistAgentSessionId(existingSessionId);
      const persistedName = getPersistedAgentStylistDisplayName();
      if (persistedName) {
        setStylistDisplayName(persistedName);
      }
      // Restore the stylist code for resumed embeds configured only with a
      // shopify page id — referral attribution must not fall back to parsing
      // the Shopify URL (which yields "pages", not a stylist code).
      // Never override a stylist code the widget was mounted with — the
      // persisted value may belong to a different stylist's page.
      const persistedCode = getPersistedAgentStylistCode();
      if (persistedCode && !stylistUniqueCode) {
        setResolvedStylistCode(persistedCode);
      }
      const hydrated = (data.display_messages || []).map((message) => ({
        role: message.role,
        content: message.content,
        images: message.images,
      }));
      if (hydrated.length > 0) {
        setMessages(hydrated);
      } else {
        setMessages([{
          role: 'assistant',
          content: GREETING,
          quick_replies: ['Book an appointment', 'Check availability', 'Product recommendation', 'Something else'],
        }]);
      }
      if (data.pending_requires_action) {
        setRequiresAction(data.pending_requires_action);
      } else if (data.booking_state?.deposit_paid || data.booking_state?.booking_id) {
        setRequiresAction(null);
      }
      return true;
    } catch {
      return false;
    }
  }, [chatApiBase, stylistUniqueCode]);

  const syncSessionFromBackend = useCallback(async () => {
    if (!sessionId) {
      return;
    }
    try {
      const data = await getChatSession(sessionId, chatApiBase);
      const hydrated = (data.display_messages || []).map((message) => ({
        role: message.role,
        content: message.content,
        images: message.images,
      }));
      if (hydrated.length > 0) {
        setMessages(hydrated);
      }
      setRequiresAction(data.pending_requires_action ?? null);
    } catch {
      // noop — voice UI keeps running even if sync fails briefly
    }
  }, [chatApiBase, sessionId]);

  const initSession = useCallback(async () => {
    try {
      const res = await createChatSession({
        stylistUniqueCode,
        shopifyStylistPageId,
        clientId,
        apiBase: chatApiBase,
        webotpHost: getWebOtpHost(),
      });
      setSessionId(res.session_id);
      if (res.stylist_unique_code) {
        setResolvedStylistCode(res.stylist_unique_code);
        persistAgentStylistCode(res.stylist_unique_code);
      }
      if (res.stylist_display_name) {
        setStylistDisplayName(res.stylist_display_name);
        persistAgentStylistDisplayName(res.stylist_display_name);
      }
      persistAgentSessionId(res.session_id);
      setMessages([{
        role: 'assistant',
        content: GREETING,
        quick_replies: ['Book an appointment', 'Check availability', 'Product recommendation', 'Something else'],
      }]);
    } catch {
      setInitError(true);
    }
  }, [chatApiBase, clientId, shopifyStylistPageId, stylistUniqueCode]);

  const sendMessage = useCallback(async (text: string, activeSessionId: string, images?: string[]) => {
    setMessages((prev) => [...prev, { role: 'user', content: text, images }]);
    setLoading(true);
    setRequiresAction(null);

    try {
      const res = await sendChatMessage(activeSessionId, text, {
        stylistUniqueCode: resolvedStylistCode || stylistUniqueCode,
        clientId,
        apiBase: chatApiBase,
        images,
        webotpHost: getWebOtpHost(),
      });
      if (res.user_image_urls?.length) {
        setMessages((prev) => {
          const updated = [...prev];
          for (let index = updated.length - 1; index >= 0; index -= 1) {
            if (updated[index].role === 'user') {
              updated[index] = { ...updated[index], images: res.user_image_urls };
              break;
            }
          }
          return updated;
        });
      }
      appendAssistantReply(res);
      if (voiceRef.current?.voiceMode) {
        if (res.reply) {
          voiceRef.current.speak(res.reply, chatApiBase);
        } else {
          voiceRef.current.resumeListening();
        }
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again or call the salon directly.' },
      ]);
      voiceRef.current?.resumeListening();
    } finally {
      setLoading(false);
    }
  }, [
    appendAssistantReply,
    chatApiBase,
    clientId,
    resolvedStylistCode,
    stylistUniqueCode,
  ]);

  const handleSend = useCallback(async (text: string, images?: string[]): Promise<boolean> => {
    if (!sessionId || loading) {
      return false;
    }
    setVoiceQuickReplies([]);
    await sendMessage(text, sessionId, images);
    return true;
  }, [loading, sendMessage, sessionId]);

  const handleVoiceQuickReply = useCallback(async (value: string): Promise<boolean> => {
    if (!sessionId || loading) {
      return false;
    }
    setVoiceQuickReplies([]);
    await sendMessage(value, sessionId);
    return true;
  }, [loading, sendMessage, sessionId]);

  const handleVoicePhotos = useCallback(async (images: string[]): Promise<boolean> => {
    if (!sessionId || loading || !images.length) {
      return false;
    }
    setVoiceQuickReplies([]);
    await sendMessage('', sessionId, images);
    return true;
  }, [loading, sendMessage, sessionId]);

  const voicePaused = loading
    || requiresAction?.type === 'otp_input'
    || requiresAction?.type === 'deposit_payment';

  const voice = useAgentVoiceConversation({
    sessionId,
    apiBase: chatApiBase,
    onTranscript: handleSend,
    onCallUpdate: syncSessionFromBackend,
    paused: voicePaused,
  });
  voiceRef.current = voice;

  // Pause the mic while an OTP/deposit card is up; resume when it clears.
  useEffect(() => {
    const t = requiresAction?.type;
    voiceRef.current?.setActionBlocked(t === 'otp_input' || t === 'deposit_payment');
  }, [requiresAction]);

  const handleClose = useCallback(() => {
    voiceRef.current?.stopVoiceMode();
    setVoiceQuickReplies([]);
    setIsOpen(false);
  }, []);

  const handleVerifyOtp = useCallback(async (code: string): Promise<boolean> => {
    if (
      !sessionId
      || requiresAction?.type !== 'otp_input'
      || !requiresAction.token
      || !requiresAction.mobile
    ) {
      return false;
    }
    try {
      const result = await verifyAgentOtp(sessionId, requiresAction.mobile, code, requiresAction.token);
      if (!result.verified) {
        return false;
      }
      setRequiresAction(null);
      const resumeMessage = result.is_new_client
        ? 'OTP verified. I am a new client.'
        : 'OTP verified.';
      await sendMessage(resumeMessage, sessionId);
      return true;
    } catch {
      return false;
    }
  }, [requiresAction, sendMessage, sessionId]);

  const otpAutofillActive = requiresAction?.type === 'otp_input';
  useWebOtpAutofill(otpAutofillActive, useCallback((code: string) => {
    void handleVerifyOtp(code);
  }, [handleVerifyOtp]));

  const handleDepositPaid = useCallback(() => {
    if (!sessionId) {
      return;
    }
    setRequiresAction(null);
    sendMessage('Deposit paid.', sessionId);
  }, [sendMessage, sessionId]);

  // Add a recommended product to the Shopify storefront cart. Only wired in
  // embed mode — /cart/add.js only exists same-origin on the store domain.
  // The referral cart attribute credits the sale to the stylist. State is
  // lifted here so progress survives card unmounts and re-adds are blocked.
  const handleAddToCart = useCallback(async (product: AgentProduct) => {
    const variantId = product.variant_id || '';
    if (!variantId) {
      return { success: false, error: 'This product cannot be added online.' };
    }
    const current = cartAddsRef.current[variantId];
    if (current === 'adding' || current === 'added') {
      return { success: true };
    }
    cartAddsRef.current[variantId] = 'adding';
    // In embed mode never fall back to the booking-site URL parser — on a
    // Shopify /pages/<slug> URL it returns "pages" and misattributes the sale.
    const referral = resolvedStylistCode
      || stylistUniqueCode
      || getPersistedAgentStylistCode()
      || (embedMode ? '' : getURLstylistCode() || '')
      || undefined;
    setCartAdds((prev) => ({ ...prev, [variantId]: 'adding' }));
    const result = await addVariantToShopifyCart(variantId, referral);
    cartAddsRef.current[variantId] = result.success ? 'added' : 'error';
    setCartAdds((prev) => ({ ...prev, [variantId]: result.success ? 'added' : 'error' }));
    if (result.success) {
      // Optimistic bump so the checkout bar shows even if the count fetch
      // fails, then reconcile with the store — a sequence guard drops
      // out-of-order responses from rapid successive adds.
      const seq = ++cartCountSeqRef.current;
      cartMutationSeqRef.current = seq;
      setCartCount((prev) => (prev ?? 0) + 1);
      refreshShopifyCartUi();
      getShopifyCartCount().then((count) => {
        if (
          count !== null
          && seq >= cartMutationSeqRef.current
          && seq > cartCountAppliedSeqRef.current
        ) {
          cartCountAppliedSeqRef.current = seq;
          setCartCount(count);
        }
      });
    }
    return result;
  }, [embedMode, resolvedStylistCode, stylistUniqueCode]);

  useEffect(() => {
    if (resumeStarted.current) {
      return;
    }
    const resume = consumeAgentResume() || (embedMode ? parseEmbedResumeFromUrl() : null);
    if (!resume?.openChat || !resume.sessionId) {
      return;
    }
    resumeStarted.current = true;
    setIsOpen(true);
    setSessionId(resume.sessionId);
    persistAgentSessionId(resume.sessionId);
    loadExistingSession(resume.sessionId).finally(() => {
      sendMessage(resume.message, resume.sessionId);
    });
  }, [embedMode, loadExistingSession, sendMessage]);

  const handleOpen = useCallback(async () => {
    setIsOpen(true);
    if (sessionId) {
      return;
    }
    const persistedSessionId = getPersistedAgentSessionId();
    if (persistedSessionId) {
      const loaded = await loadExistingSession(persistedSessionId);
      if (loaded) {
        return;
      }
    }
    initSession();
  }, [initSession, loadExistingSession, sessionId]);

  useEffect(() => {
    if (!embedMode) {
      return undefined;
    }
    const onOpenChat = () => {
      handleOpen();
    };
    window.addEventListener('husl:open-chat', onOpenChat);
    return () => {
      window.removeEventListener('husl:open-chat', onOpenChat);
    };
  }, [embedMode, handleOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const panel = panelRef.current;
    const viewport = window.visualViewport;
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    if (!panel || !viewport || !mobileQuery.matches) {
      return undefined;
    }

    const clearPanelViewportStyles = () => {
      panel.style.top = '';
      panel.style.left = '';
      panel.style.right = '';
      panel.style.bottom = '';
      panel.style.width = '';
      panel.style.height = '';
    };

    const syncPanelToViewport = () => {
      if (!mobileQuery.matches) {
        clearPanelViewportStyles();
        return;
      }

      const keyboardLikelyOpen = viewport.height < window.innerHeight * 0.75;
      if (!keyboardLikelyOpen) {
        clearPanelViewportStyles();
        return;
      }

      panel.style.top = `${viewport.offsetTop}px`;
      panel.style.left = `${viewport.offsetLeft}px`;
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
      panel.style.width = `${viewport.width}px`;
      panel.style.height = `${viewport.height}px`;
    };

    viewport.addEventListener('resize', syncPanelToViewport);
    viewport.addEventListener('scroll', syncPanelToViewport);
    syncPanelToViewport();

    return () => {
      viewport.removeEventListener('resize', syncPanelToViewport);
      viewport.removeEventListener('scroll', syncPanelToViewport);
      clearPanelViewportStyles();
    };
  }, [isOpen]);

  if (isAgentVisible !== true) {
    return null;
  }

  if (!isOpen) {
    if (embedMode) {
      return null;
    }
    return createPortal(
      <>
        <style>{AGENT_WIDGET_STYLES}</style>
        <button
          onClick={handleOpen}
          aria-label="Open booking assistant"
          className="husl-agent-fab"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M20 2H4C2.9 2 2 2.9 2 4v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" fill="currentColor" />
          </svg>
        </button>
      </>,
      document.body,
    );
  }

  return createPortal(
    <>
      <style>{AGENT_WIDGET_STYLES}</style>
      <div ref={panelRef} className="husl-agent-panel">
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 16px', background: '#2d2925', flexShrink: 0,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #4a423c 0%, #7a6e62 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, color: '#e8e2da',
          }}>H</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#f5f2ee', lineHeight: 1.2 }}>
              {stylistDisplayName || 'HUSL Booking'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', background: '#4ade80', display: 'block',
              }} />
              <span style={{ fontSize: 12, color: '#9a9088' }}>Online now</span>
            </div>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', color: '#9a9088', cursor: 'pointer',
              padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 6,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {initError ? (
          <div style={{ padding: 20, fontSize: 14, color: '#9a8f86', textAlign: 'center' }}>
            Unable to start chat. Please refresh the page or call us directly.
          </div>
        ) : voice.voiceMode ? (
          <VoiceOverlay
            isListening={voice.isListening}
            isSpeaking={voice.isSpeaking}
            loading={loading}
            isAwaitingReply={voice.isAwaitingReply}
            isGreeting={voice.isGreeting}
            voiceError={voice.voiceError}
            requiresAction={requiresAction}
            sessionId={sessionId}
            quickReplies={voiceQuickReplies}
            onEnd={voice.stopVoiceMode}
            onVerifyOtp={handleVerifyOtp}
            onDepositPaid={handleDepositPaid}
            onQuickReply={handleVoiceQuickReply}
            onSendPhotos={handleVoicePhotos}
            onAddToCart={embedMode ? handleAddToCart : undefined}
            cartState={cartAdds}
          />
        ) : (
          <ChatWindow
            messages={messages}
            loading={loading}
            requiresAction={requiresAction}
            onSend={handleSend}
            onVerifyOtp={handleVerifyOtp}
            sessionId={sessionId}
            onDepositPaid={handleDepositPaid}
            onAddToCart={embedMode ? handleAddToCart : undefined}
            cartState={cartAdds}
            cartCount={cartCount}
            voiceSupported={voice.supported}
            voiceMode={voice.voiceMode}
            isListening={voice.isListening}
            isSpeaking={voice.isSpeaking}
            isAwaitingReply={voice.isAwaitingReply}
            voiceError={voice.voiceError}
            onToggleVoice={voice.toggleVoiceMode}
          />
        )}
      </div>
    </>,
    document.body,
  );
}
